import { Component } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MemberNewsService } from './member-news.service';
import { UIBlockingService } from '../core/ui-blocking.service';
import { ToastNotificationService } from '../core/toast-notification.service';
import { MemberNewsRequest } from './DTOs/member-news-request';
import { FileUploadResult } from './DTOs/file-upload-result';
import { Router } from '@angular/router';
import { CommonLibrary } from '../core/common-library';

@Component({
  selector: 'app-submit-member-news',
    templateUrl: './submit-member-news.component.html',
    providers: [UIBlockingService]
})
export class SubmitMemberNewsComponent {

    constructor(private memberNewsService: MemberNewsService,
        private fb: FormBuilder,
        private notificationService: ToastNotificationService,
        private blockUI: UIBlockingService,
        private router: Router) {
    }
    protected readonly CommonLibrary = CommonLibrary;

    maxMainImageSize = 500000000; // 500 MB
    maxAttachmentSize = 512000000; // 512 MB
    validAttachmentTypes = ["image/jpeg", "image/jpg", "image/gif", "image/png", "application/pdf"];

    articleTitleCtrl = new FormControl("articleTitle", [Validators.required]);
    articleContentCtrl = new FormControl("articleContent", [Validators.required]);
    postingAsCtrl = new FormControl("postingAs", [Validators.required]);
    mainImageCtrl = new FormControl("mainImage", [Validators.required]);

    memberNewsForm: FormGroup = new FormGroup({
        articleTitle: this.articleTitleCtrl,
        articleContent: this.articleContentCtrl,
        postingAs: this.postingAsCtrl,
    });

    mainImageFile: File = null;
    preview = "../../assets/images/member_news_default.png";
    attachments: File[] = [];

    ngOnInit() {
        this.memberNewsForm.reset();
    }

    
    articleTitle() { return this.memberNewsForm.get('articleTitle').value }
    articleContent() { return this.memberNewsForm.get('articleContent').value }
    postingAs() { return this.memberNewsForm.get('postingAs').value }

    updateMainImage(target) {
        var files: FileList = target.files;
        var file = files.item(0);

        if (!file.type.includes("image")) {
            this.notificationService.error(file.name + " must be an image file.");
            this.mainImageFile = null;
            this.preview = "../../assets/images/member_news_default.png";
            target.value = null;
            return false;
        }
        if (file.size > this.maxMainImageSize) {
            this.notificationService.error(file.name + " is larger than the maximum file size.");
            this.mainImageFile = null;
            this.preview = "../../assets/images/member_news_default.png";
            target.value = null;
            return false;
        }

        this.mainImageFile = file;


        const reader = new FileReader();
        reader.onload = (e: any) => {
            this.preview = e.target.result;
        }
        reader.readAsDataURL(file);
        return true;
    }

    updateAttachments(target) {
        var files: FileList = target.files;
        for (let i = 0; i < files.length; i++) {
            var file = files.item(i);
            if (!this.validAttachmentTypes.includes(file.type)) {
                this.notificationService.error(file.name + " is not a valid file type.");
                target.value = null;
                continue;
            }
            if (file.size > this.maxAttachmentSize) {
                this.notificationService.error(file.name + " is larger than the maximum file size.");
                target.value = null;
                continue;
            }
            this.attachments.push(file);
        }
    }

    removeAttachment(index: number) {
        if (index >= 0) {
            this.attachments.splice(index, 1);
        }
    }

    submitNews() {
        for (var i in this.memberNewsForm.controls) {
            this.memberNewsForm.controls[i].markAsTouched();
        }
        if (!this.memberNewsForm.valid /*|| this.mainImageFile == null*/) {
            this.notificationService.error("All fields including a main image required for a news article.");
            return;
        }

        this.blockUI.start();

        if (this.mainImageFile != null || this.attachments.length > 0) {
            this.uploadFile()
                .subscribe({
                    next: (data: FileUploadResult) => {
                        this.submitMemberNews(data.mainImageId, data.attachmentIds);
                    },
                    error: (err) => {
                        console.log(err);
                        this.notificationService.error(err.error.message);
                        this.blockUI.stop();
                    }
            });
        }
        else {
            this.submitMemberNews(null, null);
        }
    }

    submitMemberNews(mainImageId: string, attachmentIds: string[]) {
        var request = new MemberNewsRequest(this.articleTitle(), this.articleContent(), this.postingAs(), mainImageId, attachmentIds);

        this.memberNewsService.submitMemberNews(request)
            .subscribe({
                next: (data) => {
                    console.log('Observable emitted the next value: ' + data);
                },
                error: (err) => {
                    console.log(err);
                    this.notificationService.error(err.error.message);
                    this.blockUI.stop();
                },
                complete: () => {
                    console.log('Observable emitted the complete notification');
                    this.blockUI.stop();
                    this.notificationService.success("News submitted successfully");
                    const url = this.router.url;
                    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
                        this.router.navigate([`/${url}`]);
                    })
                }
            });
    }

    uploadFile() {
        const uploadData = new FormData();
        if (this.mainImageFile != null) {
            uploadData.append('mainImage', this.mainImageFile, this.mainImageFile.name);
        }
        for (let i = 0; i < this.attachments.length; i++) {
            let attachment = this.attachments[i];
            uploadData.append("attachment" + i, attachment, attachment.name);
        }
        return this.memberNewsService.uploadNewsFile(uploadData);
    }
}
